import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MenubarModule } from 'primeng/menubar';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { getState } from '@ngrx/signals';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { StoryStore } from '../../../stores/story/story.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { MenuModule } from 'primeng/menu';
import { SidebarModule } from 'primeng/sidebar';
import { HeaderComponent } from '../../atoms/header/header.component';
import { GridStore } from '../../../stores/grid/grid.state';
import {
  ADDABLE_WIDGET_TYPES,
  CustomGridStackWidget,
  WIDGET_TYPE_DISPLAY,
} from '../../../services/grid-widget-config.service';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ThemeSwitcherComponent } from '../../atoms/theme-switcher/theme-switcher.component';
import { AtlasSearchComponent } from '../atlas-search/atlas-search.component';
import { FormsModule } from '@angular/forms';
import { Story } from '../../../models/story';
import { GlobalsStore } from '../../../stores/globals.state';
import { StyleClassModule } from 'primeng/styleclass';
import { Commune } from '../../../models/commune';
import { AvatarModule } from 'primeng/avatar';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { ActiveUserAvatarComponent } from '../../atoms/active-user-avatar/active-user-avatar.component';
import { TopicStore } from '../../../stores/topic/topic.state';
import { Topic } from '../../../models/topic';
import { DialogModule } from 'primeng/dialog';
import { NgClass } from '@angular/common';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { DividerModule } from 'primeng/divider';
import { TopicHelperService } from '../../../services/topic/topic-helper.service';
import { AtlasTemplateService } from '../../../services/atlas-template.service';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';
import { UnsavedChangesService } from '../../../services/unsaved-changes.service';

@Component({
  selector: 'app-bookmark-menu',
  standalone: true,
  imports: [
    MenubarModule,
    SplitButtonModule,
    ButtonModule,
    TooltipModule,
    LogoComponent,
    MenuModule,
    SidebarModule,
    HeaderComponent,
    ConfirmPopupModule,
    ConfirmDialogModule,
    ThemeSwitcherComponent,
    AtlasSearchComponent,
    FormsModule,
    StyleClassModule,
    AvatarModule,
    ActiveUserAvatarComponent,
    DialogModule,
    NgClass,
    OverlayPanelModule,
    DividerModule,
  ],
  templateUrl: './bookmark-menu.component.html',
  styleUrl: './bookmark-menu.component.scss',
})
export class BookmarkMenuComponent implements OnChanges, OnInit {
  readonly widgetTypes = ADDABLE_WIDGET_TYPES;
  readonly widgetTypeDisplay = WIDGET_TYPE_DISPLAY;
  private static readonly CATEGORY_OVERFLOW_THRESHOLD = 7;

  readonly activeUserStore = inject(ActiveUserStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly storyStore = inject(StoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly widgetStore = inject(WidgetStore);
  readonly gridStore = inject(GridStore);
  readonly globalStore = inject(GlobalsStore);
  readonly topicStore = inject(TopicStore);
  readonly topicHelper = inject(TopicHelperService);
  readonly bookmarkCategoryHelper = inject(BookmarkCategoryHelperService);
  private readonly unsavedChanges = inject(UnsavedChangesService);

  @Output() loadLayout: EventEmitter<CustomGridStackWidget[]> =
    new EventEmitter();

  @Input() activeTopic: Topic | null = null;

  @ViewChild('morePanel') morePanel?: OverlayPanel;

  menuBarItems: MenuItem[] = [];
  topItems: MenuItem[] = [];
  miscItems: MenuItem[] = [];
  topicSections: { topic: Topic; categories: BookmarkCategory[] }[] = [];
  overflowSections: { category: BookmarkCategory; items: MenuItem[] }[] = [];
  selectedOverflowIndex = 0;
  bookmarkCategories: BookmarkCategory[] = [];
  groupedBookmarks: Record<string, Bookmark[]> = {};
  groupedStories: Record<string, Story[]> = {};
  activeCommune: Commune | null = null;
  activeBookmarkId: string | null = null;
  infoVisible = false;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    protected templateService: AtlasTemplateService
  ) {
    effect(() => {
      const globalState = getState(this.globalStore);
      const gridState = getState(this.gridStore);
      const bookmarkState = getState(this.bookmarkStore);

      if (this.activeBookmarkId !== gridState.activeBookmark?._id) {
        this.activeBookmarkId = gridState.activeBookmark?._id || null;
        setTimeout(() => {
          this.initializeBookmarkButton();
        }, 100);
      }
      if (!this.activeCommune) {
        this.activeCommune = globalState.activeCommune;
        this.setActiveBookmarkCategories();
      }
      if (
        bookmarkState.trigger &&
        bookmarkState.trigger.includes('bookmarkAdded')
      ) {
        queueMicrotask(() => {
          this.initializeMenu();
        });
      }
    });
  }

  /**
   * Initializes the component and sets the initial state or configurations required during the component's lifecycle.
   * Specifically, it ensures that the active bookmark categories are set.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.setActiveBookmarkCategories();
  }

  /**
   * Handles changes to bound input properties of a component. Triggered automatically
   * by Angular when any of the `@Input` bindings of the component are changed.
   *
   * @param {SimpleChanges} changes - An object of changes to the input bindings, where each key
   * corresponds to an input property name and its value is a SimpleChange object.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTopic']) {
      this.setActiveBookmarkCategories();
    }
  }

  /**
   * Updates and sets the active bookmark categories based on the current active topic
   * or active commune configuration. If no specific configuration is provided, it falls
   * back to using all bookmark categories available in the store.
   *
   * @return {void} This method does not return any value.
   */
  private setActiveBookmarkCategories(): void {
    if (this.activeTopic && this.activeTopic.bookmarkCategories) {
      this.bookmarkCategories =
        this.bookmarkCategoryStore.getByIds(
          this.activeTopic.bookmarkCategories
        ) || [];
    } else if (this.activeCommune?.atlasConfig?.navigation?.length) {
      this.bookmarkCategories = this.bookmarkCategoryStore.getByIds(
        this.activeCommune.atlasConfig.navigation
      );
    } else {
      this.bookmarkCategories = this.bookmarkCategoryStore.asArray();
    }

    // Built unconditionally (not just while a topic is active) so the "Themen"
    // flyout - and with it the Fallback-Thema - stays reachable even with no
    // active topic (e.g. in "Erstellen" mode).
    this.topicSections = this.buildTopicSections();

    this.initializeMenu();
    this.initializeTopMenu();
  }

  /**
   * Initializes the menu with items based on bookmark categories and their respective children.
   * Iterates through bookmark categories, generates child menu items for each category,
   * and constructs the menu bar items array with the relevant data.
   *
   * @return {void} Does not return a value.
   */
  initializeMenu(): void {
    this.groupedBookmarks = this.bookmarkCategoryHelper.getGroupedByCategory();
    this.groupedStories = this.storyStore.groupedByCategory();

    const sections: { category: BookmarkCategory; items: MenuItem[] }[] = [];
    this.bookmarkCategories.forEach(bookmarkCategory => {
      const categoryChildren = this.addMenuChildren(bookmarkCategory._id);
      if (categoryChildren.length > 0) {
        sections.push({ category: bookmarkCategory, items: categoryChildren });
      }
    });

    // Beyond the threshold, further top-level category items would overflow
    // the bar - the rest move into the "Mehr" flyout. A flyout instead of a
    // nested submenu, since a third menubar level gets clipped by the
    // scrollable second level and is awkward to navigate.
    const cutoff =
      sections.length > BookmarkMenuComponent.CATEGORY_OVERFLOW_THRESHOLD
        ? BookmarkMenuComponent.CATEGORY_OVERFLOW_THRESHOLD - 1
        : sections.length;
    this.overflowSections = sections.slice(cutoff);
    this.menuBarItems = sections.slice(0, cutoff).map(section => ({
      pTooltip: section.category.title,
      label: section.category.title,
      icon: section.category.private ? 'fas fa-eye-slash' : '',
      items: section.items,
    }));
    if (this.overflowSections.length > 0) {
      this.menuBarItems.push({
        label: 'Mehr',
        icon: 'fas fa-ellipsis',
        command: (event: MenuItemCommandEvent) => {
          this.selectedOverflowIndex = this.getActiveOverflowIndex();
          this.morePanel?.toggle(event.originalEvent);
        },
      });
    }
  }

  /**
   * Index of the "Mehr" flyout section containing the active bookmark, so the
   * flyout opens where the user currently is - falls back to the first one.
   *
   * @return {number} The section index to preselect.
   */
  private getActiveOverflowIndex(): number {
    const index = this.overflowSections.findIndex(section =>
      section.items.some(
        item =>
          !!this.activeBookmarkId &&
          item.routerLink?.[1] === this.activeBookmarkId
      )
    );
    return Math.max(index, 0);
  }

  /**
   * Executes a bookmark/story entry from the "Mehr" flyout and closes it.
   * Bookmarks navigate via their `routerLink` on the anchor itself, stories
   * only carry a `command`.
   *
   * @param {MenuItem} item - The clicked entry.
   * @param {MouseEvent} event - The originating click event.
   * @return {void} Does not return a value.
   */
  selectOverflowItem(item: MenuItem, event: MouseEvent): void {
    item.command?.({ originalEvent: event, item });
    this.morePanel?.hide();
  }

  /**
   * Builds the "Themen" flyout sections: one per published topic, together with
   * the bookmark categories ("Themenbereiche") assigned to it.
   *
   * @return {{topic: Topic; categories: BookmarkCategory[]}[]} The topic sections to render in the flyout.
   */
  private buildTopicSections(): {
    topic: Topic;
    categories: BookmarkCategory[];
  }[] {
    const sections = this.topicStore
      .sorted()
      .filter(topic => this.topicHelper.isTopicVisibleForContext(topic))
      .map(topic => ({
        topic,
        categories: this.bookmarkCategoryStore.getByIds(
          topic.bookmarkCategories || []
        ),
      }))
      .filter(section => section.categories.length > 0);

    const fallbackTopic = this.topicHelper.getFallbackTopic(this.activeCommune);
    if (fallbackTopic) {
      sections.push({
        topic: fallbackTopic,
        categories: this.bookmarkCategoryStore.getByIds(
          fallbackTopic.bookmarkCategories
        ),
      });
    }

    return sections;
  }

  /**
   * Switches into the given topic and closes the "Themen" flyout.
   *
   * @param {Topic} topic - The topic to switch into.
   * @param {OverlayPanel} panel - The flyout panel to close after selection.
   * @return {void} Does not return a value.
   */
  selectTopic(topic: Topic, panel: OverlayPanel): void {
    this.globalStore.triggerAtlasEvent('loadTopic', { topic });
    panel.hide();
  }

  /**
   * Switches into the given topic like `selectTopic()`, but jumps straight to the
   * first bookmark within the clicked "Bereich" instead of the topic's own
   * start/default view - so clicking a specific category actually takes you there.
   *
   * @param {Topic} topic - The topic the category belongs to.
   * @param {BookmarkCategory} category - The clicked bookmark category.
   * @param {OverlayPanel} panel - The flyout panel to close after selection.
   * @return {void} Does not return a value.
   */
  selectCategory(
    topic: Topic,
    category: BookmarkCategory,
    panel: OverlayPanel
  ): void {
    const firstBookmark = this.bookmarkCategoryHelper
      .getBookmarksForCategory(category._id)
      .at(0);
    this.globalStore.triggerAtlasEvent('loadTopic', {
      topic,
      bookmarkId: firstBookmark?._id,
    });
    panel.hide();
  }

  /**
   * Applies a layout template from the bar's template flyout. If the canvas
   * already has real widgets, asks for confirmation first (reusing the same
   * overwrite-confirm dialog as `quickSave()`) since applying a template
   * replaces the current layout - a no-op confirmation isn't needed on the
   * still-blank Werkstatt canvas.
   *
   * @param {string} templateKey - The `GridTemplate.key` to apply.
   * @param {OverlayPanel} panel - The flyout panel to close after selection.
   * @return {void} Does not return a value.
   */
  selectTemplate(templateKey: string, panel: OverlayPanel): void {
    panel.hide();
    const apply = () =>
      this.globalStore.triggerAtlasEvent('applyTemplate', { templateKey });

    if (this.isGridEmpty) {
      apply();
    } else {
      this.confirmationDialogService.confirmBookmarkOverwrite(
        apply,
        'Aktuelle Ansicht wirklich durch die Vorlage ersetzen?'
      );
    }
  }

  /**
   * Initializes the bookmark button by setting up its related menu items, icons, and commands.
   * The menu includes options for saving bookmarks as a copy, overwriting the current view, and creating a new view.
   *
   * @return {void} Does not return a value.
   */
  private initializeBookmarkButton(): void {
    // read-only users (level 10) can't save views - no save menu at all
    if (this.activeUserStore.isReadOnly()) {
      this.topItems = [];
      return;
    }
    this.topItems = [
      {
        icon: 'fas fa-floppy-disk',
        items: [
          {
            disabled: !this.activeBookmarkId,
            icon: 'fas fa-copy',
            label: 'als Kopie speichern',
            command: event => {
              const activeBookmark = this.gridStore.activeBookmark();
              if (activeBookmark) {
                const copy: Bookmark = { ...activeBookmark };
                copy._id = '';
                this.showBookmarkForm(copy);
              }
            },
          },
          {
            disabled: !this.activeBookmarkId,
            icon: 'fas fa-pen',
            label: 'aktuelle Ansicht überschreiben',
            command: event =>
              this.showBookmarkForm(this.gridStore.activeBookmark()),
          },
          {
            icon: 'fas fa-plus-circle',
            label: 'Ansicht anlegen',
            command: event => this.showBookmarkForm(),
          },
        ],
      },
    ];
  }

  /**
   * Initializes the top menu by defining the structure and behavior of menu items.
   * The menu items include icons, labels, and associated commands or actions to be executed.
   *
   * @return {void} This method does not return any value.
   */
  private initializeTopMenu(): void {
    this.initializeBookmarkButton();
    this.miscItems = [
      {
        label: 'Info',
        sub: 'Wichtige Funktionen des Kommunal-Atlas erklärt',
        icon: 'fas fa-info-circle',
        command: () => this.showInfo(),
      },
      {
        visible: !this.activeUserStore.isLoggedIn(),
        label: 'Login',
        sub: 'Anmelden, um alle Funktionen zu nutzen',
        icon: 'fas fa-right-to-bracket',
        target: 'hyperion_login',
        routerLink: ['/login'],
      },
      {
        visible: !!this.activeUserStore.isLoggedIn(),
        label: 'Zur Datenpflege',
        sub: 'Datenpflege in neuem Tab öffnen',
        icon: 'fas fa-database',
        target: 'hyperion_administration',
        routerLink: ['/data-backend'],
      },
      {
        visible: !!this.activeUserStore.isLoggedIn(),
        label: 'Einstellungen',
        sub: 'Konfiguration in neuem Tab öffnen',
        icon: 'fas fa-cogs',
        target: '_blank',
        routerLink: [
          '/data-backend',
          { outlets: { overlay: 'konfiguration' } },
        ],
      },
      //{
      //  url: 'https://www.youtube.com/playlist?list=PLxyQdUGjPEsF8ZEsi47HHpQRI6-v-8owD',
      //  label: 'Tutorials',
      //  sub: 'YouTube-Kanal in neuem Tab öffnen',
      //  icon: 'fa-brands fa-youtube',
      //},
      {
        visible: !!this.globalStore.activeCommune()?.contactUrl,
        label: 'Kontakt',
        sub: 'Kommunen-Kontakt-Seite in neuem Tab öffnen',
        icon: 'fas fa-at',
        target: 'hyperion_kontakt',
        url: this.globalStore.activeCommune()?.contactUrl,
      },
    ];
  }

  /**
   * Generates and returns an array of menu items based on the bookmarks and stories
   * associated with the given category ID.
   *
   * @param {string} categoryId The identifier for the category to retrieve menu items for.
   * @return {MenuItem[]} An array of menu items containing bookmarks and stories for the given category.
   */
  addMenuChildren(categoryId: string): MenuItem[] {
    const bookmarks = this.groupedBookmarks[categoryId];
    const stories = this.groupedStories[categoryId];
    if (!bookmarks && !stories) return [];
    const items: MenuItem[] = [];

    // adds bookmarks
    bookmarks?.forEach(bookmark => {
      items.push({
        label: bookmark.title,
        icon: bookmark.private ? 'fas fa-eye-slash' : 'fas fa-note-sticky',
        routerLink: ['ansicht', bookmark._id],
      });
    });

    // adds stories
    stories?.forEach(story => {
      items.push({
        label: story.title,
        icon: 'fas fa-file-lines',
        //routerLink: ['bericht', story._id],
        command: (event: MenuItemCommandEvent) => {
          this.globalStore.setActiveStory(story._id);
        },
      });
    });

    return items;
  }

  /**
   * Opens the bookmark form for the user. If a bookmark is provided,
   * the form will be pre-populated for editing that bookmark.
   *
   * @param {Bookmark} [bookmarkToEdit] - An optional bookmark object to edit. If not provided, the form will allow adding a new bookmark.
   * @return {void} Does not return a value.
   */
  showBookmarkForm(bookmarkToEdit?: Bookmark): void {
    this.gridStore.openBookmarkForm(bookmarkToEdit);
  }

  /**
   * Requests a quick-save of the current layout via the 'quickSave' atlas event —
   * the actual save logic lives in AtlasGridComponent so every trigger (this menu,
   * the floating toolbar, …) shares one implementation.
   *
   * @return {void} No return value.
   */
  quickSave(): void {
    this.globalStore.triggerAtlasEvent('quickSave', {});
  }

  /**
   * Navigates to the landing page by triggering the appropriate event through the global store.
   *
   * Asks first if the current view has unsaved changes.
   *
   * @return {Promise<void>} Resolves once navigated or the user cancelled.
   */
  async goToLandingpage(): Promise<void> {
    // The landing page resets the grid without a guarded navigation.
    if (!(await this.unsavedChanges.confirmDiscard())) return;
    this.globalStore.closeActiveTopic();
    this.globalStore.triggerAtlasEvent('openLandingPage', {});
  }

  /**
   * Whether nothing is currently active - no topic and no bookmark - i.e. the
   * blank "Werkstatt" workspace reached via "Erstellen" on the landing page.
   * Communes without the Topics feature rely on a flat, always-topic-less
   * category nav (`atlasConfig.navigation`) while genuinely browsing, so this
   * only counts as blank once a bookmark is ALSO absent - otherwise their
   * normal navigation would incorrectly disappear too.
   *
   * @return {boolean} True if the bar should show its reduced "Werkstatt" state.
   */
  get isWorkshopMode(): boolean {
    return this.gridStore.isWorkshopMode();
  }

  /**
   * Whether the canvas has no real widgets yet - only (at most) the "adder"
   * tile. Used to show the template-picker hint text in the "Werkstatt"
   * label only while it's still relevant (before the user has picked a
   * template/added a widget).
   *
   * @return {boolean} True if there are no non-adder widgets yet.
   */
  get isGridEmpty(): boolean {
    return this.widgetStore.isEmpty();
  }

  /**
   * PrimeFlex classes for the main bar: the topic-tinted `bg`/`border` when the
   * active topic has a color, the neutral dashed "Werkstatt" look when nothing
   * is active, otherwise the existing static light/dark look.
   *
   * @return {string} The `styleClass` string for the `p-menubar`.
   */
  getMenuBarClasses(): string {
    const base = 'mx-1 my-2 gap-2 border-round-xl';
    if (this.isWorkshopMode) {
      return `${base} border-dashed border-2 border-400 surface-100`;
    }
    const classes = this.topicHelper.getTopicColorClasses(
      this.globalStore.activeTopic()?.color,
      this.globalStore.activeTheme()
    );
    if (classes) {
      return `${base} ${classes.bg} ${classes.border}`;
    }
    return this.globalStore.activeTheme() === 'hyperion-light'
      ? `bg-blue-50 ${base} border-blue-300`
      : `hyperion-dark ${base} border-blue-300`;
  }

  /**
   * PrimeFlex classes for the round "Themen"-switch button: a solid, theme-independent
   * accent shade of the active topic's color, or none when there's no active topic/color.
   *
   * @return {string} The class string to apply to the button.
   */
  getTopicButtonClasses(): string {
    const classes = this.topicHelper.getTopicColorClasses(
      this.globalStore.activeTopic()?.color,
      this.globalStore.activeTheme()
    );
    return classes ? `${classes.bgStrong} text-white border-none` : '';
  }

  /**
   * PrimeFlex classes for a topic's small icon badge within a flyout section -
   * a solid, theme-independent accent shade of that specific topic's color (falls
   * back to the primary color via `bg-primary` when the topic has none).
   *
   * @param {Topic} topic - The topic the badge represents.
   * @return {string} The class string to apply to the badge.
   */
  getSectionBadgeClasses(topic: Topic): string {
    const classes = this.topicHelper.getTopicColorClasses(
      topic.color,
      this.globalStore.activeTheme()
    );
    return classes ? classes.bgStrong : 'bg-primary';
  }

  showInfo(): void {
    this.infoVisible = true;
  }
}
